/**
 * Partner onboarding status context (Slice B — Feb 2026 July).
 *
 * Fetches the business-details view once when the shell mounts and
 * exposes:
 *
 *   status              — "missing" | "incomplete" | "complete"
 *   isComplete          — boolean shorthand
 *   loading             — while the first fetch is in flight
 *   refresh()           — invalidate + refetch (call after saving)
 *
 * The banner (`BusinessDetailsBanner`) and the payment-details route
 * guard both consume this context so status stays consistent without
 * each page having to fetch it independently.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const OnboardingStatusContext = createContext({
  status: "missing",
  isComplete: false,
  loading: true,
  refresh: () => {},
});

export function OnboardingStatusProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState("missing");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (user?.role !== "partner") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/partner/business-details");
      setStatus(data?.status || "missing");
    } catch {
      // Never block the shell — if the API is down we just hide the banner
      setStatus("missing");
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <OnboardingStatusContext.Provider
      value={{
        status,
        isComplete: status === "complete",
        loading,
        refresh: load,
      }}
    >
      {children}
    </OnboardingStatusContext.Provider>
  );
}

export function useOnboardingStatus() {
  return useContext(OnboardingStatusContext);
}
